/**
 * Wag and Buckle — UGC Video Carousel
 * ----------------------------------
 * Free, vendor-neutral replacement for Novel/partnrUP/Tolstoy/etc.
 * Pairs with: sections/cg-ugc-carousel.liquid + assets/cg-ugc.css
 *
 * Behavior:
 *  - Lazy-init each section via IntersectionObserver (rootMargin 300px)
 *  - Click a tile → open native <dialog> lightbox with the video
 *  - Optional shop drawer with one-click add-to-cart (Dawn-pattern)
 *  - Keyboard nav, focus trap (native <dialog>), Escape to close
 *  - Arrow buttons scroll the track (when arrows are enabled)
 *  - prefers-reduced-motion respected
 *  - Multiple sections per page supported (each has its own dialog)
 *
 * Cart drawer fallback chain:
 *  1. <cart-notification>.renderContents(data)  (Dawn default)
 *  2. <cart-drawer>.renderContents(data)        (Dawn cart-drawer mode)
 *  3. window.location.href = '/cart'            (final fallback)
 */

(function () {
  'use strict';

  var LAZY_ROOT_MARGIN = '300px';
  var SCROLL_BY_RATIO = 0.8;        // arrow click scrolls 80% of viewport width
  var ADD_SUCCESS_MS = 1600;        // how long the CTA shows "Added"
  var REOPEN_REFOCUS_DELAY = 60;    // ms before returning focus on close

  /**
   * Lazy-init: hydrate a section only when it's near the viewport.
   */
  function lazyInit() {
    var sections = document.querySelectorAll('[data-cg-ugc]:not([data-cg-ugc-init])');
    if (!sections.length) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            obs.unobserve(entry.target);
            initSection(entry.target);
          }
        });
      }, { rootMargin: LAZY_ROOT_MARGIN });
      sections.forEach(function (s) { observer.observe(s); });
    } else {
      sections.forEach(initSection);
    }
  }

  /**
   * Initialize a single carousel section.
   * @param {HTMLElement} section
   */
  function initSection(section) {
    if (section.dataset.cgUgcInit === '1') return;
    section.dataset.cgUgcInit = '1';

    var grid = section.querySelector('[data-cg-ugc-grid]');
    var dialog = section.querySelector('[data-cg-ugc-dialog]');
    var video = section.querySelector('[data-cg-ugc-video]');
    var caption = section.querySelector('[data-cg-ugc-caption]');
    var player = section.querySelector('[data-cg-ugc-player]');
    var closeBtn = section.querySelector('[data-cg-ugc-close]');
    var dialogPrev = section.querySelector('[data-cg-ugc-dialog-prev]');
    var dialogNext = section.querySelector('[data-cg-ugc-dialog-next]');
    var shop = section.querySelector('[data-cg-ugc-shop]');
    var shopLink = section.querySelector('[data-cg-ugc-product-url]');
    var shopImage = section.querySelector('[data-cg-ugc-product-image]');
    var shopTitle = section.querySelector('[data-cg-ugc-product-title]');
    var shopPrice = section.querySelector('[data-cg-ugc-product-price]');
    var addBtn = section.querySelector('[data-cg-ugc-add]');
    var addLabel = section.querySelector('[data-cg-ugc-add-label]');
    var prevBtn = section.querySelector('[data-cg-ugc-prev]');
    var nextBtn = section.querySelector('[data-cg-ugc-next]');

    if (!grid || !dialog || !video || !closeBtn) {
      console.warn('[cg-ugc] section missing required nodes', section);
      return;
    }

    // ---------- Configurable labels (from data attrs) ----------
    var CTA_LABEL = section.getAttribute('data-cg-ugc-cta-label') || 'Add to cart';
    var CTA_SUCCESS_LABEL = section.getAttribute('data-cg-ugc-cta-success-label') || 'Added ✓';
    var AUTOPLAY_TILES = section.getAttribute('data-cg-ugc-autoplay-tiles') === 'true';

    // ---------- Tile registry (for in-lightbox prev/next) ----------
    var triggers = Array.prototype.slice.call(grid.querySelectorAll('[data-cg-ugc-open]'));

    // ---------- Lightbox state ----------
    var lastTrigger = null;
    var activeIndex = -1;
    var activeVariantId = '';
    var activeProductTitle = '';
    var addResetTimer = 0;

    // ---------- Open / close ----------

    function openTile(trigger) {
      var src = trigger.getAttribute('data-video-src');
      if (!src) return;

      lastTrigger = trigger;
      activeIndex = triggers.indexOf(trigger);
      video.src = src;

      activeVariantId = trigger.getAttribute('data-variant-id') || '';
      activeProductTitle = trigger.getAttribute('data-product-title') || '';

      // Caption (from tile's data-caption)
      var captionText = trigger.getAttribute('data-caption') || '';
      if (caption) {
        if (captionText) {
          caption.textContent = captionText;
          caption.hidden = false;
        } else {
          caption.textContent = '';
          caption.hidden = true;
        }
      }

      if (activeVariantId && shop) {
        var url = trigger.getAttribute('data-product-url') || '#';
        var image = trigger.getAttribute('data-product-image') || '';
        var price = trigger.getAttribute('data-product-price') || '';

        if (shopLink) shopLink.setAttribute('href', url);
        if (shopImage) {
          if (image) {
            shopImage.setAttribute('src', image);
            shopImage.setAttribute('alt', activeProductTitle);
            shopImage.style.display = '';
          } else {
            shopImage.style.display = 'none';
          }
        }
        if (shopTitle) shopTitle.textContent = activeProductTitle;
        if (shopPrice) shopPrice.textContent = price;
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.classList.remove('is-loading', 'is-success');
        }
        if (addLabel) addLabel.textContent = CTA_LABEL;

        shop.hidden = false;
      } else if (shop) {
        shop.hidden = true;
      }

      updateDialogNav();

      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      }

      // Try to autoplay; will fail without a user gesture in some browsers,
      // in which case the native controls let the user start it manually.
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () { /* user can press play */ });
      }
    }

    function openIndex(i) {
      if (i < 0 || i >= triggers.length) return;
      openTile(triggers[i]);
    }

    function goNext() { openIndex(activeIndex + 1); }
    function goPrev() { openIndex(activeIndex - 1); }

    function updateDialogNav() {
      if (dialogPrev) dialogPrev.disabled = activeIndex <= 0;
      if (dialogNext) dialogNext.disabled = activeIndex >= triggers.length - 1;
    }

    function closeTile() {
      try { video.pause(); } catch (_) {}
      video.removeAttribute('src');
      try { video.load(); } catch (_) {}

      if (typeof dialog.close === 'function') {
        try { dialog.close(); } catch (_) {}
      } else {
        dialog.removeAttribute('open');
      }

      window.clearTimeout(addResetTimer);
      if (addBtn) addBtn.classList.remove('is-loading', 'is-success');
      if (addLabel) addLabel.textContent = CTA_LABEL;

      activeIndex = -1;

      if (lastTrigger) {
        var t = lastTrigger;
        setTimeout(function () { try { t.focus(); } catch (_) {} }, REOPEN_REFOCUS_DELAY);
      }
    }

    // Delegated click — supports buttons added later via dynamic content.
    // Tile clicks now hand off to the Dreamland-style drawer (cg-ugc-drawer.js)
    // via a CustomEvent. The legacy in-section <dialog> lightbox is kept as a
    // fallback when the drawer snippet/JS isn't on the page.
    grid.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-cg-ugc-open]');
      if (!btn || !grid.contains(btn)) return;
      event.preventDefault();

      var drawerEl = document.querySelector('[data-cg-ugc-drawer]');
      if (drawerEl) {
        var idxAttr = btn.getAttribute('data-cg-ugc-tile-index');
        var idx = idxAttr != null ? parseInt(idxAttr, 10) : triggers.indexOf(btn);
        document.dispatchEvent(new CustomEvent('cg-ugc:open-drawer', {
          detail: {
            sectionId: section.dataset.sectionId || section.id,
            tileIndex: idx,
            videoSrc: btn.getAttribute('data-cg-ugc-video-src') || btn.getAttribute('data-video-src') || '',
            posterSrc: btn.getAttribute('data-cg-ugc-poster-src') || '',
            creator: btn.getAttribute('data-cg-ugc-creator') || '',
            caption: btn.getAttribute('data-cg-ugc-caption') || btn.getAttribute('data-caption') || '',
            productHandle: btn.getAttribute('data-cg-ugc-product-handle') || ''
          }
        }));
        return;
      }

      // Fallback to legacy in-section lightbox if the drawer isn't present
      openTile(btn);
    });

    closeBtn.addEventListener('click', closeTile);

    // Escape key & system close
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeTile();
    });

    // Click on backdrop closes — backdrop click bubbles with target === dialog
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeTile();
    });

    // ---------- In-lightbox prev/next ----------
    if (dialogPrev) dialogPrev.addEventListener('click', goPrev);
    if (dialogNext) dialogNext.addEventListener('click', goNext);

    // Keyboard arrows inside the dialog
    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') { event.preventDefault(); goNext(); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); goPrev(); }
    });

    // Swipe gestures on the player (touch + pointer)
    if (player) {
      var SWIPE_THRESHOLD = 50;
      var startX = 0, startY = 0, tracking = false;
      player.addEventListener('touchstart', function (e) {
        if (!e.touches || !e.touches.length) return;
        startX = e.touches[0].clientX; startY = e.touches[0].clientY; tracking = true;
      }, { passive: true });
      player.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        var t = (e.changedTouches && e.changedTouches[0]) || null;
        if (!t) return;
        var dx = t.clientX - startX;
        var dy = t.clientY - startY;
        if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) goNext(); else goPrev();
        }
      });
    }

    // ---------- Add to cart ----------

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        if (!activeVariantId) return;
        addBtn.disabled = true;
        addBtn.classList.add('is-loading');
        addBtn.classList.remove('is-success');

        var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var sectionsToRender = ['cart-notification', 'cart-drawer', 'cart-icon-bubble'];

        var body = JSON.stringify({
          items: [{ id: Number(activeVariantId), quantity: 1 }],
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        });

        fetch(rootPath + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
          body: body,
        })
          .then(function (res) {
            if (!res.ok) throw new Error('cart/add failed: ' + res.status);
            return res.json();
          })
          .then(function (data) {
            handleCartResponse(data);
            addBtn.classList.remove('is-loading');
            addBtn.classList.add('is-success');
            if (addLabel) addLabel.textContent = CTA_SUCCESS_LABEL;
            window.clearTimeout(addResetTimer);
            addResetTimer = window.setTimeout(function () {
              if (addBtn) {
                addBtn.disabled = false;
                addBtn.classList.remove('is-success');
              }
              if (addLabel) addLabel.textContent = CTA_LABEL;
            }, ADD_SUCCESS_MS);
          })
          .catch(function (err) {
            console.error('[cg-ugc] add to cart failed', err);
            addBtn.classList.remove('is-loading');
            addBtn.disabled = false;
            window.location.href = '/cart';
          });
      });
    }

    function handleCartResponse(data) {
      // 1. Try cart-notification (Dawn default)
      var notif = document.querySelector('cart-notification');
      if (notif && typeof notif.renderContents === 'function') {
        try { notif.renderContents(data); return; } catch (_) {}
      }
      // 2. Try cart-drawer (Dawn drawer mode)
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        try { drawer.renderContents(data); return; } catch (_) {}
      }
      // 3. Try opening any element with a known drawer trigger
      var openDrawerEvent = new CustomEvent('cart:added', { bubbles: true, detail: data });
      document.dispatchEvent(openDrawerEvent);
      // 4. Bump the cart bubble manually if present
      var bubble = document.querySelector('cart-icon-bubble');
      if (bubble && data && data.sections && data.sections['cart-icon-bubble']) {
        try {
          var html = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
          var newBubble = html.querySelector('cart-icon-bubble');
          if (newBubble) bubble.innerHTML = newBubble.innerHTML;
        } catch (_) {}
      }
    }

    // ---------- Arrows ----------

    if (prevBtn || nextBtn) {
      // Arrows are always visible and wrap around at the ends — clicking
      // Next at the end jumps to the start, clicking Prev at the start
      // jumps to the end. Gives the carousel an "infinite scroll" feel
      // without cloning tiles.
      var updateArrows = function () {
        if (!grid) return;
        if (prevBtn) {
          prevBtn.hidden = false;
          prevBtn.removeAttribute('aria-disabled');
        }
        if (nextBtn) {
          nextBtn.hidden = false;
          nextBtn.removeAttribute('aria-disabled');
        }
      };

      var scrollByDir = function (dir) {
        var amount = grid.clientWidth * SCROLL_BY_RATIO * dir;
        var atStart = grid.scrollLeft <= 4;
        var atEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 4;
        if (dir > 0 && atEnd) {
          grid.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }
        if (dir < 0 && atStart) {
          grid.scrollTo({ left: grid.scrollWidth, behavior: 'smooth' });
          return;
        }
        grid.scrollBy({ left: amount, behavior: 'smooth' });
      };

      if (prevBtn) prevBtn.addEventListener('click', function () { scrollByDir(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { scrollByDir(1); });

      grid.addEventListener('scroll', throttleRaf(updateArrows), { passive: true });
      window.addEventListener('resize', throttleRaf(updateArrows));
      // First paint
      updateArrows();
    }

    // ---------- Tile autoplay (muted) on scroll into view ----------
    // Tiles are rendered with preload="none" and NO <source> element.
    // We attach the source lazily — only when a tile is about to enter the
    // viewport — so the page doesn't fire off N video requests on load.
    if (AUTOPLAY_TILES) {
      var tileVideos = section.querySelectorAll('[data-cg-ugc-tile-video]');
      if (tileVideos.length && 'IntersectionObserver' in window) {
        // Phase 1: hydrate (attach <source> + preload metadata) ~600px out
        var hydrateObserver = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            hydrateTileVideo(entry.target);
            obs.unobserve(entry.target);
          });
        }, { rootMargin: '600px 0px' });

        // Phase 2: play/pause when actually visible
        var playObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var v = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio > 0.4) {
              // Make sure source is wired in case we skipped hydrate
              // (e.g. fast scroll past the 600px margin).
              hydrateTileVideo(v);
              var p = v.play();
              if (p && typeof p.catch === 'function') p.catch(function () {});
            } else {
              try { v.pause(); } catch (_) {}
            }
          });
        }, { threshold: [0, 0.4, 0.8] });

        Array.prototype.forEach.call(tileVideos, function (v) {
          hydrateObserver.observe(v);
          playObserver.observe(v);
        });
      } else if (tileVideos.length) {
        // Old-browser fallback: hydrate + play all immediately
        Array.prototype.forEach.call(tileVideos, function (v) {
          hydrateTileVideo(v);
          try { v.play(); } catch (_) {}
        });
      }
    }
  }

  function hydrateTileVideo(v) {
    if (!v || v.dataset.cgUgcHydrated === '1') return;
    var src = v.getAttribute('data-cg-ugc-tile-video-src');
    if (!src) return;
    var source = document.createElement('source');
    source.src = src;
    source.type = 'video/mp4';
    v.appendChild(source);
    // preload="auto" — download the full clip in the background once the
    // tile is near the viewport. When the user later taps the tile, the
    // drawer opens the same MP4 URL and the browser serves it from cache
    // instead of starting a fresh network fetch.
    v.preload = 'auto';
    try { v.load(); } catch (_) {}
    v.dataset.cgUgcHydrated = '1';
  }

  // ---------- utils ----------

  function throttleRaf(fn) {
    var ticking = false;
    return function () {
      var ctx = this;
      var args = arguments;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(function () {
          fn.apply(ctx, args);
          ticking = false;
        });
      }
    };
  }

  // ---------- boot ----------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', lazyInit);
  } else {
    lazyInit();
  }

  // Re-init when Shopify reloads a section in the theme editor
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', lazyInit);
    document.addEventListener('shopify:section:select', lazyInit);
  }
})();
